import React, { useEffect, useState, useRef } from 'react';
import { User, Users, FileCheck, Loader, CheckCircle2, ShieldCheck } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { FaDownload } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import './adminDashboard.css';
import { getDashboardData, getRecentNotifications, getCandidateDashboardReport } from '../../../services/admin/api';

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    totalClients: 0,
    activeEmployees: 0,
    inactiveEmployees: 0,
    workInProgressCandidates: 0,
    completedCandidates: 0,
    verifiedCandidates: 0,
  });
  const [activityFeed, setActivityFeed] = useState([]);
  const [reports, setReports] = useState([]);   // ✅ dynamic reports
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const cardsRef = useRef([]);
  const navigate = useNavigate();

  const username = localStorage.getItem('user_name') || '';

  // Fetch Dashboard + Notifications + Reports
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await getDashboardData();
        //console.log('📊 Dashboard API Response:', response);
        if (response.data && response.data.status && response.data.data) {
          setDashboardData(response.data.data);
        } else {
          setError('Failed to fetch dashboard data.');
        }
      } catch (err) {
        setError('Something went wrong while fetching dashboard data.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    const fetchActivityFeed = async () => {
      try {
        const response = await getRecentNotifications();
        //console.log('📩 Notifications API Response:', response);

        if (response.status && response.data) {
          setActivityFeed(response.data);
        } else {
          setActivityFeed([]);
        }
      } catch (err) {
        console.error('❌ Error fetching activity feed:', err);
        setActivityFeed([]);
      }
    };

    const fetchReports = async () => {
      try {
        const response = await getCandidateDashboardReport();
        //console.log("📑 Reports API Response:", response);

        if (response && Array.isArray(response)) {
          setReports(response);
        } else {
          setReports([]);
        }
      } catch (err) {
        console.error("❌ Error fetching reports:", err);
        setReports([]);
      }
    };

    fetchDashboardData();
    fetchActivityFeed();
    fetchReports();
  }, []);

  if (loading)
    return <div style={{ textAlign: 'center', padding: '50px', fontSize: '1.2rem', color: '#4f46e5' }}>Loading dashboard data...</div>;

  if (error)
    return <div style={{ color: 'red', textAlign: 'center', padding: '50px', fontSize: '1.2rem' }}>{error}</div>;

  const stats = [
    { title: 'Total Clients', count: dashboardData.totalClients, icon: <User size={32} color="#4f46e5" />, path: '/admin/clients' },
    { title: 'Active Employees', count: dashboardData.activeEmployees, icon: <Users size={32} color="#16a34a" />, path: '/admin/active-employee' },
    { title: 'Inactive Employees', count: dashboardData.inactiveEmployees, icon: <FileCheck size={32} color="#f97316" />, path: '/admin/inactive-employee' },
    { title: 'Work In Progress', count: dashboardData.workInProgressCandidates, icon: <Loader size={32} color="#eab308" />, path: null },
    { title: 'Completed', count: dashboardData.completedCandidates, icon: <CheckCircle2 size={32} color="#10b981" />, path: null },
    { title: 'Verified', count: dashboardData.verifiedCandidates, icon: <ShieldCheck size={32} color="#0ea5e9" />, path: null },
  ];

  const pieChartData = [
    { name: 'Active', value: dashboardData.activeEmployees },
    { name: 'Inactive', value: dashboardData.inactiveEmployees },
    { name: 'WIP', value: dashboardData.workInProgressCandidates },
    { name: 'Completed', value: dashboardData.completedCandidates },
    { name: 'Verified', value: dashboardData.verifiedCandidates },
  ];

  const PIE_COLORS = ['#4f46e5', '#16a34a', '#f97316', '#eab308', '#a4de6c'];

  const recentCandidateActivities = [
    { name: 'John Doe', status: 'Profile Updated', time: '5 mins ago', img: 'https://i.pravatar.cc/150?img=25' },
    { name: 'Jane Smith', status: 'Documents Submitted', time: '1 hour ago', img: 'https://i.pravatar.cc/150?img=26' },
  ];

  const formSubmissions = [
    { name: 'Saindhev', date: '25 June', status: 'Request BGC', img: 'https://i.pravatar.cc/150?img=1' },
    { name: 'Mahima', date: '22 June', status: 'Request BGC', img: 'https://i.pravatar.cc/150?img=2' },
  ];

  return (
    <div className="dashboard-container">
      <div className="welcome-section">
        <h1 className="welcome-title">Welcome back, {username}</h1>
        <button
          className="add-candidate-button"
          onClick={() => navigate('/admin/add-employee')}
        >
          + Add Employee
        </button>
      </div>

      <div className="dashboard-grid">
        {stats.map((stat, index) => (
          <div
            className={`card dashboard-card ${stat.path ? 'clickable-card' : ''}`}
            key={index}
            ref={el => cardsRef.current[index] = el}
            onClick={() => stat.path && navigate(stat.path)}
          >
            <div style={{ marginBottom: '12px' }}>{stat.icon}</div>
            <h3 className="card-title">{stat.title}</h3>
            <div className="card-count">{stat.count}</div>
          </div>
        ))}
      </div>

      <div className="main-dashboard-content">
        {/* Overall Candidates Status */}
        <div className="card overall-status-card">
          <h2 className="card-header">Overall candidates status</h2>
          <div className="overall-status-content">
            <div className="pie-chart-wrapper">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    fill="#8884d8"
                    paddingAngle={5}
                    dataKey="value"
                    labelLine={false}
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name, props) => [`${value}`, props.payload.name]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="pie-chart-legend vertical-legend">
              {pieChartData.map((entry, index) => (
                <div key={`legend-${index}`} className="legend-item">
                  <span className="legend-color" style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}></span>
                  {entry.name}: {entry.value}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Activity Feed */}
        <div className="card activity-feed-card">
          <h2 className="card-header">Activity feed</h2>
          <div className="scrollable-card-list">
            <div className="scroll-animation-wrapper">
              {activityFeed.length > 0 ? (
                activityFeed.map((activity, index) => (
                  <div key={`activity-${index}`} className="list-card">
                    <span className="activity-text">
                      Candidate {activity.candidateId}: {activity.message}
                    </span>
                    <span className="activity-time">
                      {new Date(activity.createdAt).toLocaleString()}
                    </span>
                  </div>
                ))
              ) : (
                <div className="list-card">No recent notifications</div>
              )}
            </div>
          </div>
        </div>

        {/* <div className="bottom-sections-container">
          <div className="scrollable-card-list">
            <h2 className="list-title">Recent Candidates</h2>
            <div className="marquee-content-wrapper">
              <div className="scroll-animation-wrapper">
                {[...recentCandidateActivities, ...recentCandidateActivities].map((item, index) => (
                  <div className="list-card" key={`rc-${index}`}>
                    <div className="list-card-left">
                      <img src={item.img} alt="avatar" className="profile-image" />
                      <div>
                        <div className="client-name">{item.name}</div>
                        <div className="status-text">{item.status} ({item.time})</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="scrollable-card-list">
            <h2 className="list-title">Form Submissions</h2>
            <div className="marquee-content-wrapper">
              <div className="scroll-animation-wrapper">
                {[...formSubmissions, ...formSubmissions].map((item, index) => (
                  <div className="list-card" key={`fs-${index}`}>
                    <div className="list-card-left">
                      <img src={item.img} alt="avatar" className="profile-image" />
                      <div>
                        <div className="client-name">{item.name}</div>
                        <div className="status-text">Submitted on {item.date}</div>
                      </div>
                    </div>
                    <button className="client-button">{item.status}</button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="scrollable-card-list">
            <h2 className="list-title">Reports</h2>
            <div className="marquee-content-wrapper">
              <div className="scroll-animation-wrapper">
                {reports.length > 0 ? (
                  reports.map((item, index) => (
                    <div className="list-card" key={`rprt-${index}`}>
                      <div className="list-card-left">
                        <img
                          src={`https://i.pravatar.cc/150?img=${index + 30}`}
                          alt="avatar"
                          className="profile-image"
                        />
                        <div>
                          <div className="status-text">Candidate: {item.candidateId}</div>
                          <div className="status-text">
                            {item.reportDate ? new Date(item.reportDate).toLocaleDateString() : 'N/A'}
                          </div>
                        </div>
                      </div>
                     <span className={`report-status ${item.verificationStatus?.toLowerCase().replace('_', '-')}`}>
                      {item.verificationStatus}
                    </span>

                    </div>
                  ))
                ) : (
                  <div className="list-card">No reports available</div>
                )}
              </div>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default Dashboard;
